import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { AlertTriangle, X } from "lucide-react";
import { toast } from "sonner";

const REFRESH_INTERVAL_MS = 60_000;

/**
 * ScrapflyAlertBanner — red top-of-admin ping when today's Scrapfly
 * credit burn has crossed the daily threshold set on the
 * `wua-scrapfly-watchdog` daemon. Deduped server-side per UTC day, so
 * this shows exactly once per spike until an admin dismisses it.
 */
export default function ScrapflyAlertBanner() {
  const [alert, setAlert] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/scrapfly/alert");
      setAlert(r.data?.alert || null);
    } catch {
      /* silent — banner is opportunistic */
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(load, REFRESH_INTERVAL_MS);
    return () => clearInterval(t);
  }, [load]);

  const dismiss = async () => {
    try {
      await api.post("/admin/scrapfly/alert/ack");
      setAlert(null);
      toast.success("Alert acknowledged — will re-fire on the next daily spike.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not dismiss alert");
    }
  };

  if (!alert) return null;

  return (
    <div
      data-testid="scrapfly-alert-banner"
      className="mb-6 border border-rose-300 bg-rose-50 text-rose-950 rounded-xl p-4 flex items-start gap-3 shadow-sm"
    >
      <AlertTriangle className="h-5 w-5 text-rose-700 shrink-0 mt-0.5" />
      <div className="flex-1">
        <div className="font-semibold text-sm">
          Scrapfly credit-burn alert · {alert.date}
        </div>
        <div className="text-xs mt-1 text-rose-900/90">
          Today's burn is{" "}
          <strong>{alert.credits?.toLocaleString?.() || alert.credits} credits</strong>{" "}
          across <strong>{alert.calls}</strong> calls — over the daily
          threshold of {alert.threshold?.toLocaleString?.() || alert.threshold}.
          A daemon may be looping.{" "}
          <a href="#scrapfly-usage-tile" className="underline">
            Check the Scrapfly usage tile
          </a>{" "}
          or run <code className="bg-rose-100 px-1 rounded">supervisorctl status</code>.
        </div>
      </div>
      <button
        data-testid="scrapfly-alert-dismiss-btn"
        onClick={dismiss}
        className="text-rose-700 hover:text-rose-900 shrink-0 p-1"
        title="Acknowledge"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
